import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
} from '@heroicons/react/24/outline';
import useAuth from '../../hooks/useAuth';
import { useCapabilities } from '../../auth/CapabilitiesContext';
import usePendingApprovalsCount from '../../hooks/usePendingApprovalsCount';
import { useFeatureConfig } from '../../hooks/useFeatureConfig';
import { useGoogleSheetsConfig } from '../../hooks/useGoogleSheetsConfig';
import { HIDDEN_MENU_PATHS, CUSTOMER_NAME } from '../../config';
import NavIcon from '../ui/NavIcon';
import { SECTIONS, NAV_ITEMS } from '../../config/nav-config';

const LS_KEY = 'sidebarCollapsedSections';
const RAIL_LS_KEY = 'sidebarRailCollapsed';

const BADGE_SELECTORS = {
  pendingApprovals: (ctx) => ctx.pendingApprovalsCount,
};

function loadCollapsed() {
  try {
    const raw =
      typeof window !== 'undefined' && window.localStorage
        ? window.localStorage.getItem(LS_KEY)
        : null;
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function saveCollapsed(set) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(LS_KEY, JSON.stringify([...set]));
  } catch {
    /* quota / private mode — no-op */
  }
}

function loadRailCollapsed() {
  try {
    return typeof window !== 'undefined' && window.localStorage
      ? window.localStorage.getItem(RAIL_LS_KEY) === '1'
      : false;
  } catch {
    return false;
  }
}

function saveRailCollapsed(value) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(RAIL_LS_KEY, value ? '1' : '0');
  } catch {
    /* quota / private mode — no-op */
  }
}

function SectionHeader({ sectionId, labelText, isCollapsed, badgeCount, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={!isCollapsed}
      aria-controls={`section-${sectionId}`}
      className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-700"
    >
      <span>{labelText}</span>
      <span className="flex items-center gap-2">
        {isCollapsed && badgeCount > 0 && (
          <span
            data-testid={`section-${sectionId}-badge`}
            className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-yellow-100 text-yellow-800"
          >
            {badgeCount}
          </span>
        )}
        <ChevronRightIcon
          className={`h-4 w-4 transform transition-transform duration-200 motion-reduce:transition-none ${
            isCollapsed ? '' : 'rotate-90'
          }`}
          aria-hidden="true"
        />
      </span>
    </button>
  );
}

function Sidebar({ isOpen, onClose }) {
  const { t } = useTranslation(['menu', 'ui']);
  const location = useLocation();
  const { hasAnyRole } = useAuth();
  const { can } = useCapabilities();
  const { logisticsOpsEnabled, assetMgmtEnabled, wmsEnabled } = useFeatureConfig();
  const { enabled: googleSheetsEnabled, spreadsheetUrl: googleSheetsUrl } = useGoogleSheetsConfig();
  // Only admins can read the approvals endpoint (route requiredRoles: [ADMIN]).
  // Gate polling so scoped roles don't fire a guaranteed-403 request.
  const pendingApprovalsCount = usePendingApprovalsCount(hasAnyRole(['admin']));
  const [collapsed, setCollapsedState] = useState(loadCollapsed);
  // Desktop-only "rail" collapse (whole column to icons) — independent from the
  // per-section accordion above, which mobile keeps using at full width.
  const [railCollapsed, setRailCollapsed] = useState(loadRailCollapsed);

  const toggleRail = useCallback(() => {
    setRailCollapsed((prev) => {
      const next = !prev;
      saveRailCollapsed(next);
      return next;
    });
  }, []);

  const ctx = { pendingApprovalsCount, googleSheetsEnabled, googleSheetsUrl };

  const toggleSection = useCallback((id) => {
    setCollapsedState((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      saveCollapsed(next);
      return next;
    });
  }, []);

  const isActive = (path) =>
    location.pathname === path
      ? 'bg-primary-100 text-primary-900'
      : 'text-secondary-600 hover:bg-gray-50';

  const isAllowed = useCallback(
    (item) => {
      if (item.path.startsWith('/logistics/') && !logisticsOpsEnabled) return false;
      if (item.path.startsWith('/assets') && !assetMgmtEnabled) return false;
      // Backend module for /stock-movements (and its third-party-stock/etc.
      // siblings) only mounts when FEATURE_WMS_ENABLED=true (app.module.ts).
      // The route isn't module-gated in routeConfig.js — same as logistics/
      // assets above — so hiding it here is what stops every deployment
      // without that flag from 404ing on page load.
      if (item.path === '/stock-movements' && !wmsEnabled) return false;
      if (HIDDEN_MENU_PATHS.includes(item.path)) return false;
      // Um predicado só, compartilhado com CommandPalette e RequireCapability.
      // Vale igual para item interno e externo — o catálogo é que sabe a
      // diferença, e para os externos esconder o atalho não bloqueia o destino.
      return can(item.capability);
    },
    [can, logisticsOpsEnabled, assetMgmtEnabled, wmsEnabled],
  );

  const visibleSections = useMemo(
    () =>
      SECTIONS.map((section) => {
        const items = NAV_ITEMS.filter(
          (i) =>
            i.section === section.id &&
            isAllowed(i) &&
            (!i.conditional || ctx[i.conditional]),
        );
        const badgeCount = items.reduce(
          (sum, item) =>
            sum + (item.badge ? (BADGE_SELECTORS[item.badge]?.(ctx) ?? 0) : 0),
          0,
        );
        return { ...section, items, badgeCount };
      }).filter((s) => s.items.length > 0),
    // ctx is built from pendingApprovalsCount/googleSheetsEnabled/googleSheetsUrl
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAllowed, pendingApprovalsCount, googleSheetsEnabled, googleSheetsUrl],
  );

  // Auto-expand the section containing the active route (one-shot per route change).
  // Only depends on pathname; collapsed state read via setCollapsedState's prev arg.
  useEffect(() => {
    const activeItem = NAV_ITEMS.find((i) => i.path === location.pathname);
    if (!activeItem) return;
    setCollapsedState((prev) => {
      if (!prev.has(activeItem.section)) return prev; // identity unchanged → no re-render
      const next = new Set(prev);
      next.delete(activeItem.section);
      saveCollapsed(next);
      return next;
    });
  }, [location.pathname]);

  const renderNavItem = (item, isRail) => {
    const badgeFn = item.badge ? BADGE_SELECTORS[item.badge] : null;
    const badgeValue = badgeFn ? badgeFn(ctx) : null;
    const label = t(item.labelKey);

    if (item.external) {
      const href = item.hrefFrom ? ctx[item.hrefFrom] : item.path;
      if (!href) return null; // conditional flag true but URL not yet resolved — skip this render
      return (
        <a
          key={item.path}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={isOpen ? onClose : undefined}
          title={isRail ? label : undefined}
          aria-label={isRail ? label : undefined}
          className={`text-secondary-600 hover:bg-gray-50 group flex items-center py-2 text-sm font-medium rounded-md ${
            isRail ? 'justify-center px-2' : 'px-2'
          }`}
        >
          <NavIcon name={item.iconName} className={isRail ? 'h-5 w-5' : 'mr-3 h-5 w-5'} />
          {!isRail && <span className="flex-1">{label}</span>}
        </a>
      );
    }

    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={isOpen ? onClose : undefined}
        title={isRail ? label : undefined}
        aria-label={isRail ? label : undefined}
        className={`${isActive(item.path)} group flex items-center py-2 text-sm font-medium rounded-md ${
          isRail ? 'justify-center px-2' : 'px-2'
        }`}
      >
        <span className="relative">
          <NavIcon name={item.iconName} className={isRail ? 'h-5 w-5' : 'mr-3 h-5 w-5'} />
          {isRail && badgeValue > 0 && (
            <span
              className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-yellow-500"
              aria-hidden="true"
            />
          )}
        </span>
        {!isRail && <span className="flex-1">{label}</span>}
        {!isRail && badgeValue > 0 && (
          <span className="ml-auto inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
            {badgeValue}
          </span>
        )}
      </Link>
    );
  };

  const renderSidebarContent = (isRail, showRailToggle) => (
    <div className="flex flex-col h-0 flex-1 border-r border-gray-200 bg-white">
      <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
        <div className="flex-1 px-3 space-y-3">
          {visibleSections.map((section) => {
            // Rail mode flattens groups to icons only — a header with no label
            // has nothing to toggle, so every item in it stays visible and the
            // per-section accordion state is ignored (not cleared, just unused
            // while the rail is collapsed).
            const isCollapsed = !isRail && collapsed.has(section.id);
            return (
              <div key={section.id} className="space-y-1">
                {isRail ? (
                  <div className="border-t border-gray-100 first:border-t-0 pt-1 first:pt-0" />
                ) : (
                  <SectionHeader
                    sectionId={section.id}
                    labelText={t(section.labelKey)}
                    isCollapsed={isCollapsed}
                    badgeCount={section.badgeCount}
                    onToggle={() => toggleSection(section.id)}
                  />
                )}
                {!isCollapsed && (
                  <div id={`section-${section.id}`} className="space-y-1">
                    {section.items.map((item) => renderNavItem(item, isRail))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      {CUSTOMER_NAME && (
        <div
          className={`border-t border-gray-200 px-4 py-3 flex items-center gap-2 ${
            isRail ? 'justify-center px-2' : ''
          }`}
          data-testid="sidebar-brand-footer"
        >
          <img
            src={`${process.env.PUBLIC_URL}/logo192.png`}
            alt=""
            title={isRail ? CUSTOMER_NAME : undefined}
            className="h-6 w-6 rounded flex-shrink-0"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
          {!isRail && <span className="text-xs text-secondary-600 truncate">{CUSTOMER_NAME}</span>}
        </div>
      )}
      {showRailToggle && (
        <div className="border-t border-gray-200 p-2">
          <button
            type="button"
            onClick={toggleRail}
            aria-label={isRail ? t('ui:sidebar.expand') : t('ui:sidebar.collapse')}
            title={isRail ? t('ui:sidebar.expand') : t('ui:sidebar.collapse')}
            className={`w-full flex items-center rounded-md py-2 text-secondary-600 hover:bg-gray-50 hover:text-secondary-900 ${
              isRail ? 'justify-center' : 'justify-start gap-2 px-2'
            }`}
          >
            {isRail ? (
              <ChevronDoubleRightIcon className="h-5 w-5" aria-hidden="true" />
            ) : (
              <>
                <ChevronDoubleLeftIcon className="h-5 w-5" aria-hidden="true" />
                <span className="text-sm font-medium">{t('ui:sidebar.collapse')}</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <>
      {isOpen && (
        <div className="md:hidden">
          <div
            className="fixed inset-0 bg-gray-600 bg-opacity-75 z-40"
            onClick={onClose}
          />
          <div className="fixed inset-y-0 left-0 z-50 flex flex-col w-64 bg-white">
            <div className="flex justify-end p-3 border-b border-gray-200 bg-white">
              <button
                type="button"
                onClick={onClose}
                aria-label="Close sidebar"
                className="text-secondary-600 hover:text-secondary-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-white"
              >
                <svg
                  className="h-6 w-6"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 6l12 12M6 18L18 6"
                  />
                </svg>
              </button>
            </div>
            {renderSidebarContent(false, false)}
          </div>
        </div>
      )}
      <div className="hidden md:flex md:flex-shrink-0">
        <div
          className={`flex flex-col transition-[width] duration-200 motion-reduce:transition-none ${
            railCollapsed ? 'w-16' : 'w-64'
          }`}
        >
          {renderSidebarContent(railCollapsed, true)}
        </div>
      </div>
    </>
  );
}

export default Sidebar;
